// ===================================================================
// Tipos de curso — cadastro institucional
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';

const emptyForm = {
  code: '',
  name: '',
  sort_order: '0',
  is_active: true,
};

export const CourseTypesManager = React.memo(() => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const { addNotification } = useNotifications();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getCourseTypes(true);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError('Erro ao carregar tipos de curso');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      code: row.code || '',
      name: row.name || '',
      sort_order: String(row.sort_order ?? 0),
      is_active: row.is_active !== false,
    });
    setError('');
    setShowModal(true);
  };

  const save = async () => {
    const name = (form.name || '').trim();
    if (!name) {
      setError('Nome é obrigatório');
      return;
    }
    const payload = {
      name,
      sort_order: parseInt(form.sort_order, 10) || 0,
      is_active: Boolean(form.is_active),
    };
    if (!editing) {
      payload.code = (form.code || '').trim();
    }
    try {
      setLoading(true);
      if (editing) {
        const { error: updateError } = await apiClient.updateCourseType(editing.id, payload);
        if (updateError) {
          setError(updateError);
          return;
        }
        addNotification('Sucesso', 'Tipo de curso atualizado', 'success');
      } else {
        const { error: createError } = await apiClient.createCourseType(payload);
        if (createError) {
          setError(createError);
          return;
        }
        addNotification('Sucesso', 'Tipo de curso criado', 'success');
      }
      setShowModal(false);
      load();
    } catch {
      setError('Erro ao salvar tipo de curso');
    } finally {
      setLoading(false);
    }
  };

  if (loading && rows.length === 0) {
    return <LoadingSpinner text="Carregando tipos de curso..." />;
  }

  return (
    <div style={styles.contentContainer}>
      <div style={{ ...styles.flexBetween, marginBottom: '16px' }}>
        <div>
          <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
            Tipos de curso
          </h2>
          <p style={{ fontSize: '14px', color: '#6B7280', marginTop: '8px' }}>
            Cadastre os tipos disponíveis ao criar cursos (Integrado, Subsequente, FIC, etc.).
          </p>
        </div>
        <button type="button" style={{ ...styles.button, ...styles.buttonPrimary }} onClick={openNew}>
          Novo tipo
        </button>
      </div>

      {error && !showModal && <Alert type="error" message={error} onClose={() => setError('')} />}

      <div style={styles.card}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#F3F4F6' }}>
              <th style={{ padding: 10, textAlign: 'left' }}>Nome</th>
              <th style={{ padding: 10, textAlign: 'left' }}>Código</th>
              <th style={{ padding: 10, textAlign: 'center' }}>Ordem</th>
              <th style={{ padding: 10, textAlign: 'center' }}>Ativo</th>
              <th style={{ padding: 10, textAlign: 'center' }}>Em uso</th>
              <th style={{ padding: 10 }} />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 16, textAlign: 'center', color: '#6B7280' }}>
                  Nenhum tipo cadastrado. Clique em &quot;Novo tipo&quot; para começar.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <td style={{ padding: 10 }}>{row.name}</td>
                  <td style={{ padding: 10, fontFamily: 'monospace', fontSize: 12 }}>{row.code}</td>
                  <td style={{ padding: 10, textAlign: 'center' }}>{row.sort_order ?? 0}</td>
                  <td style={{ padding: 10, textAlign: 'center' }}>{row.is_active ? 'Sim' : 'Não'}</td>
                  <td style={{ padding: 10, textAlign: 'center' }}>{row.in_use ? 'Sim' : '—'}</td>
                  <td style={{ padding: 10, textAlign: 'right' }}>
                    <button
                      type="button"
                      style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
                      onClick={() => openEdit(row)}
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Editar tipo de curso' : 'Novo tipo de curso'}>
        {error && showModal && <Alert type="error" message={error} onClose={() => setError('')} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={styles.label}>Nome *</label>
            <input
              style={styles.input}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Ex.: Integrado, FIC, Subsequente"
            />
          </div>
          {!editing && (
            <div>
              <label style={styles.label}>Código (opcional)</label>
              <input
                style={styles.input}
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toLowerCase() }))}
                placeholder="Gerado automaticamente a partir do nome se vazio"
              />
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                Identificador interno (ex.: integrado, fic). Não pode ser alterado depois.
              </div>
            </div>
          )}
          {editing && (
            <div>
              <label style={styles.label}>Código</label>
              <input style={{ ...styles.input, backgroundColor: '#F3F4F6' }} value={form.code} readOnly />
            </div>
          )}
          <div>
            <label style={styles.label}>Ordem de exibição</label>
            <input
              type="number"
              style={styles.input}
              value={form.sort_order}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
            />
          </div>
          <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
            />
            Ativo (disponível ao cadastrar novos cursos)
          </label>
          <div style={styles.formActions}>
            <button type="button" style={{ ...styles.button, ...styles.buttonSecondary }} onClick={() => setShowModal(false)}>
              Cancelar
            </button>
            <button type="button" style={{ ...styles.button, ...styles.buttonPrimary }} onClick={save}>
              Salvar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
});
